import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Popover from '@radix-ui/react-popover';
import Icon from '../../../../components/AppIcon';

const WEEK_DAYS = [
  { short: 'MO', full: 'Monday' },
  { short: 'TU', full: 'Tuesday' },
  { short: 'WE', full: 'Wednesday' },
  { short: 'TH', full: 'Thursday' },
  { short: 'FR', full: 'Friday' },
  { short: 'SA', full: 'Saturday' },
  { short: 'SU', full: 'Sunday' },
];

export interface RecurringScheduleFilters {
  title: string;
  days: string[];
  timeFrom: string;
  timeTo: string;
  status: 'all' | 'active' | 'inactive';
  departmentIds: string[];
  userIds: string[];
}

interface FilterBarProps {
  filters: RecurringScheduleFilters;
  onFiltersChange: (filters: RecurringScheduleFilters) => void;
  departments: string[];
  users: { id: string; name: string; department?: string }[];
}

const FilterBar: React.FC<FilterBarProps> = ({ filters, onFiltersChange, departments, users }) => {
  const { t } = useTranslation();

  // Keep a ref to the latest filters + callback to avoid stale closures in debounce
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const onChangeRef = useRef(onFiltersChange);
  onChangeRef.current = onFiltersChange;

  // Debounced title search (300ms)
  const [titleInput, setTitleInput] = useState(filters.title);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    debounceRef.current = setTimeout(() => {
      if (titleInput !== filtersRef.current.title) {
        onChangeRef.current({ ...filtersRef.current, title: titleInput });
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [titleInput]);

  const hasActiveFilters = filters.title || filters.days.length > 0 || filters.timeFrom || filters.timeTo || filters.status !== 'all' || filters.departmentIds.length > 0 || filters.userIds.length > 0;

  const update = (partial: Partial<RecurringScheduleFilters>) => {
    onFiltersChange({ ...filters, ...partial });
  };

  const clearAll = () => {
    setTitleInput('');
    onFiltersChange({
      title: '', days: [], timeFrom: '', timeTo: '',
      status: 'all', departmentIds: [], userIds: [],
    });
  };

  const toggleDay = (day: string) => {
    const next = filters.days.includes(day)
      ? filters.days.filter(d => d !== day)
      : [...filters.days, day];
    update({ days: next });
  };

  const filteredUsers = useMemo(() => {
    if (filters.departmentIds.length === 0) return users;
    return users.filter(u => filters.departmentIds.includes(u.department || ''));
  }, [users, filters.departmentIds]);

  const toggleDepartment = (dept: string) => {
    const next = filters.departmentIds.includes(dept)
      ? filters.departmentIds.filter(d => d !== dept)
      : [...filters.departmentIds, dept];
    update({ departmentIds: next, userIds: [] });
  };

  const toggleUser = (userId: string) => {
    const next = filters.userIds.includes(userId)
      ? filters.userIds.filter(id => id !== userId)
      : [...filters.userIds, userId];
    update({ userIds: next });
  };

  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Title search — debounced */}
      <div className="relative">
        <Icon name="Search" size={14} className="absolute start-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder={t('FilterByTitle', { defaultValue: 'Filter by title...' })}
          value={titleInput}
          onChange={(e) => setTitleInput(e.target.value)}
          className="ps-8 pe-3 py-2 border border-border rounded-md text-sm w-48 bg-background"
          data-testid="filter-title"
        />
      </div>

      {/* Day toggles */}
      <div className="flex gap-1" data-testid="filter-days">
        {WEEK_DAYS.map(day => (
          <button
            key={day.short}
            onClick={() => toggleDay(day.short)}
            className={`px-2 py-1.5 rounded text-xs font-medium transition-colors ${
              filters.days.includes(day.short)
                ? 'bg-primary text-primary-foreground'
                : 'bg-card border border-border text-muted-foreground hover:bg-muted'
            }`}
            title={day.full}
            data-testid={`filter-day-${day.short}`}
          >
            {day.short}
          </button>
        ))}
      </div>

      {/* Time range */}
      <div className="flex items-center gap-1">
        <input
          type="time"
          value={filters.timeFrom}
          onChange={(e) => update({ timeFrom: e.target.value })}
          className="px-2 py-1.5 border border-border rounded-md text-sm bg-background"
          title={t('TimeFrom', { defaultValue: 'From' })}
          data-testid="filter-time-from"
        />
        <span className="text-muted-foreground text-xs">–</span>
        <input
          type="time"
          value={filters.timeTo}
          onChange={(e) => update({ timeTo: e.target.value })}
          className="px-2 py-1.5 border border-border rounded-md text-sm bg-background"
          title={t('TimeTo', { defaultValue: 'To' })}
          data-testid="filter-time-to"
        />
      </div>

      {/* Status */}
      <select
        value={filters.status}
        onChange={(e) => update({ status: e.target.value as RecurringScheduleFilters['status'] })}
        className="px-3 py-2 border border-border rounded-md text-sm bg-background"
        data-testid="filter-status"
      >
        <option value="all">{t('All')}</option>
        <option value="active">{t('Active')}</option>
        <option value="inactive">{t('Inactive')}</option>
      </select>

      {/* Department — multi-select via Radix Popover with checkboxes */}
      <Popover.Root>
        <Popover.Trigger asChild>
          <button
            className="px-3 py-2 border border-border rounded-md text-sm bg-background flex items-center gap-1"
            data-testid="filter-department"
          >
            {filters.departmentIds.length > 0
              ? `${filters.departmentIds.length} dept(s)`
              : t('All departments')}
            <Icon name="ChevronDown" size={14} />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content className="w-56 bg-popover border border-border rounded-lg shadow-lg z-50 p-2 max-h-60 overflow-y-auto" sideOffset={4}>
            {departments.map(dept => (
              <label key={dept} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.departmentIds.includes(dept)}
                  onChange={() => toggleDepartment(dept)}
                  className="rounded border-border"
                />
                <span className="truncate">{dept}</span>
              </label>
            ))}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {/* User — multi-select via extracted sub-component */}
      <UserFilterPopover
        filteredUsers={filteredUsers}
        selectedUserIds={filters.userIds}
        onToggleUser={toggleUser}
        t={t}
      />

      {/* Clear */}
      {hasActiveFilters && (
        <button
          onClick={clearAll}
          className="text-sm text-primary hover:underline"
          data-testid="filter-clear"
        >
          {t('ClearFilters', { defaultValue: 'Clear Filters' })}
        </button>
      )}
    </div>
  );
};

// Extracted sub-component so search state is local and resets on close
const UserFilterPopover: React.FC<{
  filteredUsers: { id: string; name: string; department?: string }[];
  selectedUserIds: string[];
  onToggleUser: (id: string) => void;
  t: (key: string, opts?: any) => string;
}> = ({ filteredUsers, selectedUserIds, onToggleUser, t }) => {
  const [userSearch, setUserSearch] = useState('');
  const visibleUsers = useMemo(() => {
    if (!userSearch) return filteredUsers;
    const q = userSearch.toLowerCase();
    return filteredUsers.filter(u => u.name.toLowerCase().includes(q));
  }, [filteredUsers, userSearch]);

  return (
    <Popover.Root onOpenChange={() => setUserSearch('')}>
      <Popover.Trigger asChild>
        <button
          className="px-3 py-2 border border-border rounded-md text-sm bg-background flex items-center gap-1"
          data-testid="filter-user"
        >
          {selectedUserIds.length > 0
            ? `${selectedUserIds.length} user(s)`
            : t('AllUsers', { defaultValue: 'All users' })}
          <Icon name="ChevronDown" size={14} />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className="w-64 bg-popover border border-border rounded-lg shadow-lg z-50 p-2" sideOffset={4}>
          <input
            type="text"
            placeholder={t('SearchUsers', { defaultValue: 'Search users...' })}
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            className="w-full px-2 py-1.5 border border-border rounded-md text-sm bg-background mb-2"
            data-testid="filter-user-search"
          />
          <div className="max-h-48 overflow-y-auto space-y-0.5">
            {visibleUsers.map(u => (
              <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedUserIds.includes(u.id)}
                  onChange={() => onToggleUser(u.id)}
                  className="rounded border-border"
                />
                <span className="truncate">{u.name}</span>
                {u.department && (
                  <span className="text-xs text-muted-foreground ms-auto">{u.department}</span>
                )}
              </label>
            ))}
            {visibleUsers.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-2">{t('NoResults')}</p>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default FilterBar;
export const INITIAL_FILTERS: RecurringScheduleFilters = {
  title: '', days: [], timeFrom: '', timeTo: '',
  status: 'all', departmentIds: [], userIds: [],
};
